// Phase 5 client state store.
//
// Architectural rule (non-negotiable): the `recipe` field is the
// server-blessed validated cookpit file. The client never mutates it.
// Per-item check state, the latest anchored plan, and planToken are
// *overlay* — separate fields that may change as the user interacts.
//
// The store does not persist across reloads (no `persist` middleware in
// Phase 5). Validation, attestation, anchoring stay server-side; the
// client only ever calls `/api/validate-recipe` and `/api/anchor`
// through `apps/web/src/api/client.ts`.

import { create } from 'zustand';
import type {
  AnchoredPlan,
  ClassifiedPlan,
  Cookpit,
  Nudge,
  RecipeMeta,
  ServeTime,
  ValidateRecipeError,
} from '@cookchow/shared';
import { setActiveScheme, type SchemeName } from '../design/theme';
import type { VoicePersona } from '../cooking/voicePersonas';
import type { CueSound } from '../cooking/sounds';
import { computePrepBudget, shouldDerivePrepChecklist } from '../cooking/derivePrep';

// Alias for the schema-derived cookpit file type. Sourced from the
// auto-generated namespace re-export in `@cookchow/shared`.
export type CookpitFile = Cookpit.CookpitCookingFileV32;

export type PrereqKind = 'ingredients' | 'utensils' | 'equipment';

export type VisitedSection = 'planner' | 'hotspots' | 'notes';

export type Handedness = 'left' | 'right';

export interface CheckedState {
  ingredients: Record<string, boolean>;
  utensils: Record<string, boolean>;
  equipment: Record<string, boolean>;
}

export interface VisitedState {
  planner: boolean;
  hotspots: boolean;
  notes: boolean;
}

export interface SetValidatedRecipeArgs {
  recipe: Readonly<CookpitFile>;
  recipeId: string;
  meta: RecipeMeta;
}

// 'idle'         — no cook in progress.
// 'countdown'    — a Planner Countdown cook anchored to a future serve time:
//                  the plan is anchored and held DORMANT (timers parked, the
//                  cook screen read-only) until the backward-computed cook
//                  start instant (`cookStartAt`) arrives, at which point the
//                  cook flips into its first real phase below.
// 'prep'         — anchored, timed preCook/prepCook split (PrepPane).
// 'prepChecklist'— derived, untimed PrepCook checklist from prerequisites
//                  when no timed prep phase is declared (DECISIONS §E.7).
export type CookingState =
  | 'idle'
  | 'countdown'
  | 'prep'
  | 'prepChecklist'
  | 'liveCook'
  | 'complete';

export interface AppState {
  // file selection / validation
  recipeFile: File | null;
  // Read-only client copy of the validated cookpit file. The store
  // exposes no setter that mutates fields on this object; the only
  // writes happen via `setValidatedRecipe` (full replacement on a
  // successful validation) and `clearRecipe` (back to idle).
  recipe: Readonly<CookpitFile> | null;
  recipeId: string | null;
  meta: RecipeMeta | null;
  validationError: ValidateRecipeError | null;

  // prereq tick state — overlay only
  checked: CheckedState;

  // informational-section read state. Set when the user opens
  // Planner / Hotspots / Notes. Reset on new recipe upload.
  visited: VisitedState;

  // Cook-screen handedness preference. Session-only — resets to
  // 'right' when a new recipe is opened. Controls which edge of the
  // pane the Look-Back slider and the per-task checkbox sit on.
  handedness: Handedness;

  // Cook-screen sound preference. When true, task-activation cues
  // (whistle / bell / drum — see cooking/sounds.ts) play as tasks enter
  // their active window. Session-only; resets to its default when a new
  // recipe is opened, mirroring `handedness`.
  soundEnabled: boolean;

  // Spoken-announcement preferences (cooking/sounds.ts, voicePersonas.ts).
  // `speechRate` is the speech-speed multiplier (0.5–1.75; default 1.0);
  // `voicePersona` is one of the five voice characters (default 'female').
  // Session-only, reset with a new recipe like handedness/soundEnabled.
  speechRate: number;
  voicePersona: VoicePersona;
  // Activation cue clip (cooking/sounds.ts). One of the seven selectable
  // chimes; default 'whistle' (the referee whistle). Session-only.
  cueSound: CueSound;

  // Colour scheme, chosen on the Home screen. Session-only (defaults to
  // 'calm'; not persisted across reloads) but kept across recipe open/clear
  // so a Home choice carries into the cook. Mirrored into the active palette
  // via setActiveScheme.
  colorScheme: SchemeName;

  // planner / anchor
  serveTime: ServeTime | null;
  anchoredPlan: AnchoredPlan | null;
  planToken: string | null;
  anchorError: string | null;

  // Phase 6 — cooking phase overlay. None of these touch the cookpit
  // task projection on `anchoredPlan.schedule`; they are session state
  // only.
  cookingState: CookingState;
  clockOffset: number; // ms (server − client)
  // Planner Countdown only: the backward-computed wall-clock instant the
  // cook is scheduled to begin (Date.now() epoch). null for an immediate
  // Start Cooking. While `cookingState === 'countdown'` the cook screen holds
  // dormant until `Date.now()` reaches this instant; the phase clocks are then
  // seeded from it so elapsed time counts from the planned start, not the flip.
  cookStartAt: number | null;
  prepStartedAt: number | null; // Date.now() epoch when prep timer began
  liveCookStartedAt: number | null; // Date.now() epoch at transition
  prepFrozenAt: number | null; // seconds — footer chip value
  classified: ClassifiedPlan | null;
  nudge: Nudge | null;
  lastServerSeenAt: number | null; // performance.now() of last good response
  indicatorConfirmed: boolean;
  completedTaskIds: string[];

  // Derived PrepCook checklist (DECISIONS §E.7). Untimed prep surfaced
  // from cookpit.prerequisites when no timed prep phase is declared. These
  // are app-rendered checklist items, never cookpit tasks — confirmed ids
  // live separately from `completedTaskIds` and never hit /complete-task.
  prepBudgetSeconds: number | null; // advisory budget timer total
  prepBudgetApproximate: boolean; // true ⇒ default budget, not a declared prepTime
  confirmedPrepIds: string[]; // prerequisites entry ids ticked off

  // actions
  setRecipeFile: (f: File | null) => void;
  setValidationError: (e: ValidateRecipeError | null) => void;
  setValidatedRecipe: (args: SetValidatedRecipeArgs) => void;
  clearRecipe: () => void;
  toggleCheck: (kind: PrereqKind, id: string) => void;
  markVisited: (section: VisitedSection) => void;
  setHandedness: (h: Handedness) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setSpeechRate: (rate: number) => void;
  setVoicePersona: (persona: VoicePersona) => void;
  setCueSound: (cue: CueSound) => void;
  setColorScheme: (s: SchemeName) => void;
  setServeTime: (t: ServeTime | null) => void;
  setAnchoredPlan: (plan: AnchoredPlan, token: string) => void;
  setAnchorError: (msg: string | null) => void;
  startCookingReady: () => boolean;

  // Phase 6 actions. `enterCooking()` starts the cook for real, choosing the
  // initial mode (timed prep / derived checklist / liveCook) from the file.
  // An explicit `startAt` epoch seeds the phase clocks (used by the Countdown
  // flip so elapsed time runs from the planned start); defaults to now.
  enterCooking: (startAt?: number) => void;
  // Begin a Planner Countdown cook: hold the (already anchored) plan dormant
  // until `cookStartAt`. The cook screen renders read-only until then.
  startCountdown: (cookStartAt: number) => void;
  markPrepStarted: (atEpoch: number) => void;
  transitionToLiveCook: (prepFrozenSecs: number, atEpoch: number) => void;
  setClassified: (c: ClassifiedPlan, nudge: Nudge, serverNow: string) => void;
  markTaskCompleted: (taskId: string) => void;
  confirmPrepItem: (id: string) => void;
  confirmIndicator: () => void;
  closeSession: () => void;
}

const EMPTY_CHECKED: CheckedState = {
  ingredients: {},
  utensils: {},
  equipment: {},
};

const EMPTY_VISITED: VisitedState = {
  planner: false,
  hotspots: false,
  notes: false,
};

// Helper: read the count of items in each kind off the recipe so the
// `startCookingReady` derivation has a baseline to compare against.
function listIds(recipe: Readonly<CookpitFile> | null, kind: PrereqKind): string[] {
  if (!recipe) return [];
  const arr = recipe.cookpit[kind] as { id: string }[] | undefined;
  return (arr ?? []).map((x) => x.id);
}

// Pure readiness derivation: the Start Cooking gate is "every ingredient,
// utensil and equipment id is ticked". Exposed as a selector so the button
// SUBSCRIBES to it through the same `useSyncExternalStore` snapshot as the
// prereq counters. Reading this imperatively (e.g. via a `get()`-backed
// store method called during render) can tear under React's concurrent /
// StrictMode rendering — letting the counters paint "all ticked" while the
// button still reads a stale, incomplete snapshot. Subscribing makes a
// counter/button disagreement structurally impossible.
export function selectStartCookingReady(state: Pick<AppState, 'recipe' | 'checked'>): boolean {
  const { recipe, checked } = state;
  if (!recipe) return false;
  for (const kind of ['ingredients', 'utensils', 'equipment'] as const) {
    const ids = listIds(recipe, kind);
    if (ids.length === 0) continue;
    for (const id of ids) {
      if (!checked[kind][id]) return false;
    }
  }
  return true;
}

export const useAppStore = create<AppState>((set, get) => ({
  recipeFile: null,
  recipe: null,
  recipeId: null,
  meta: null,
  validationError: null,
  checked: EMPTY_CHECKED,
  visited: EMPTY_VISITED,
  handedness: 'right',
  soundEnabled: true,
  speechRate: 1,
  voicePersona: 'female',
  cueSound: 'whistle',
  colorScheme: 'calm',
  serveTime: null,
  anchoredPlan: null,
  planToken: null,
  anchorError: null,
  cookingState: 'idle',
  clockOffset: 0,
  cookStartAt: null,
  prepStartedAt: null,
  liveCookStartedAt: null,
  prepFrozenAt: null,
  classified: null,
  nudge: null,
  lastServerSeenAt: null,
  indicatorConfirmed: false,
  completedTaskIds: [],
  prepBudgetSeconds: null,
  prepBudgetApproximate: false,
  confirmedPrepIds: [],

  setRecipeFile: (f) => set({ recipeFile: f, validationError: null }),
  setValidationError: (e) => set({ validationError: e }),
  setValidatedRecipe: ({ recipe, recipeId, meta }) => {
    set({
      recipe,
      recipeId,
      meta,
      validationError: null,
      // Fresh recipe — reset all overlay state so stale ticks from a
      // previous file can't bleed across.
      checked: { ingredients: {}, utensils: {}, equipment: {} },
      visited: { planner: false, hotspots: false, notes: false },
      handedness: 'right',
      soundEnabled: true,
      speechRate: 1,
      voicePersona: 'female',
      cueSound: 'whistle',
      serveTime: null,
      anchoredPlan: null,
      planToken: null,
      anchorError: null,
      cookingState: 'idle',
      cookStartAt: null,
      prepStartedAt: null,
      liveCookStartedAt: null,
      prepFrozenAt: null,
      classified: null,
      nudge: null,
      lastServerSeenAt: null,
      indicatorConfirmed: false,
      completedTaskIds: [],
      prepBudgetSeconds: null,
      prepBudgetApproximate: false,
      confirmedPrepIds: [],
    });
  },
  clearRecipe: () => {
    set({
      recipe: null,
      recipeId: null,
      meta: null,
      validationError: null,
      checked: { ingredients: {}, utensils: {}, equipment: {} },
      visited: { planner: false, hotspots: false, notes: false },
      handedness: 'right',
      soundEnabled: true,
      speechRate: 1,
      voicePersona: 'female',
      cueSound: 'whistle',
      serveTime: null,
      anchoredPlan: null,
      planToken: null,
      anchorError: null,
      cookingState: 'idle',
      cookStartAt: null,
      prepStartedAt: null,
      liveCookStartedAt: null,
      prepFrozenAt: null,
      classified: null,
      nudge: null,
      lastServerSeenAt: null,
      indicatorConfirmed: false,
      completedTaskIds: [],
      prepBudgetSeconds: null,
      prepBudgetApproximate: false,
      confirmedPrepIds: [],
    });
  },
  toggleCheck: (kind, id) =>
    set((state) => {
      const prev = state.checked[kind];
      const next = { ...prev, [id]: !prev[id] };
      return {
        checked: {
          ...state.checked,
          [kind]: next,
        },
      };
    }),
  markVisited: (section) =>
    set((state) => {
      if (state.visited[section]) return {} as Partial<AppState>;
      return { visited: { ...state.visited, [section]: true } };
    }),
  setHandedness: (h) => set({ handedness: h }),
  setSoundEnabled: (enabled) => set({ soundEnabled: enabled }),
  setSpeechRate: (rate) => set({ speechRate: rate }),
  setVoicePersona: (persona) => set({ voicePersona: persona }),
  setCueSound: (cue) => set({ cueSound: cue }),
  // Swap the active palette synchronously *before* the state update, so the
  // re-render that follows already reads the new scheme's colours.
  setColorScheme: (s) => {
    setActiveScheme(s);
    set({ colorScheme: s });
  },
  setServeTime: (t) => set({ serveTime: t }),
  setAnchoredPlan: (plan, token) =>
    set({ anchoredPlan: plan, planToken: token, anchorError: null }),
  setAnchorError: (msg) => set({ anchorError: msg }),
  // Imperative form, kept for non-render callers. Components must SUBSCRIBE
  // via `selectStartCookingReady` instead (see Prereqs.tsx) — calling this
  // during render reads outside the subscription and can tear.
  startCookingReady: () => selectStartCookingReady(get()),

  enterCooking: (startAt) => {
    const { recipe } = get();
    if (!recipe) return;
    // Explicit start instant (the Countdown flip passes the planned cook
    // start so elapsed time runs from it); otherwise begin now.
    const startedAt = startAt ?? Date.now();
    // Three-way entry (DECISIONS §E.7):
    //   1. A declared preCook/prepCook phase → anchored, timed Prep mode.
    //   2. No timed phase but prerequisites prep → derived PrepCook checklist.
    //   3. Neither → straight to LiveCook (README §6.7.1).
    const hasTimedPrep = !!recipe.cookpit.preCook || !!recipe.cookpit.prepCook;
    const base = {
      prepFrozenAt: null,
      indicatorConfirmed: false,
      completedTaskIds: [],
      confirmedPrepIds: [],
    };
    if (hasTimedPrep) {
      set({
        ...base,
        cookingState: 'prep',
        prepStartedAt: startedAt,
        liveCookStartedAt: null,
        prepBudgetSeconds: null,
        prepBudgetApproximate: false,
      });
      return;
    }
    if (shouldDerivePrepChecklist(recipe)) {
      const budget = computePrepBudget(recipe);
      set({
        ...base,
        cookingState: 'prepChecklist',
        prepStartedAt: startedAt,
        liveCookStartedAt: null,
        prepBudgetSeconds: budget.seconds,
        prepBudgetApproximate: budget.approximate,
      });
      return;
    }
    set({
      ...base,
      cookingState: 'liveCook',
      prepStartedAt: null,
      liveCookStartedAt: startedAt,
      prepBudgetSeconds: null,
      prepBudgetApproximate: false,
    });
  },

  startCountdown: (cookStartAt) =>
    set({
      cookingState: 'countdown',
      cookStartAt,
      // Dormant: no phase clock runs yet, and any prior overlay is cleared so
      // the read-only countdown screen starts from a clean slate. The real
      // phase + its seeded clock are chosen by enterCooking(cookStartAt) when
      // the start instant arrives (see Cook.tsx).
      prepStartedAt: null,
      liveCookStartedAt: null,
      prepFrozenAt: null,
      prepBudgetSeconds: null,
      prepBudgetApproximate: false,
      indicatorConfirmed: false,
      completedTaskIds: [],
      confirmedPrepIds: [],
    }),

  markPrepStarted: (atEpoch) => set({ prepStartedAt: atEpoch }),

  transitionToLiveCook: (prepFrozenSecs, atEpoch) =>
    set({
      cookingState: 'liveCook',
      prepFrozenAt: prepFrozenSecs,
      liveCookStartedAt: atEpoch,
    }),

  setClassified: (classified, nudge, serverNow) => {
    const clientNow = Date.now();
    const serverMs = Date.parse(serverNow);
    const offset = Number.isFinite(serverMs) ? serverMs - clientNow : 0;
    set({
      classified,
      nudge,
      clockOffset: offset,
      lastServerSeenAt: performance.now(),
    });
  },

  markTaskCompleted: (taskId) => {
    const prev = get().completedTaskIds;
    if (prev.includes(taskId)) return;
    set({ completedTaskIds: [...prev, taskId] });
  },

  confirmPrepItem: (id) => {
    const prev = get().confirmedPrepIds;
    if (prev.includes(id)) return;
    set({ confirmedPrepIds: [...prev, id] });
  },

  confirmIndicator: () => set({ indicatorConfirmed: true, cookingState: 'complete' }),

  closeSession: () =>
    set({
      cookingState: 'idle',
      cookStartAt: null,
      prepStartedAt: null,
      liveCookStartedAt: null,
      prepFrozenAt: null,
      classified: null,
      nudge: null,
      indicatorConfirmed: false,
      completedTaskIds: [],
      prepBudgetSeconds: null,
      prepBudgetApproximate: false,
      confirmedPrepIds: [],
      lastServerSeenAt: null,
    }),
}));
